// 手机子页面：记录表按字段展示，比较表保留横向关系；扩大原生按钮触控区域，电脑布局及业务事件不变。
import React, { useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { getPhase, PHASES, COMPLETED_PHASE } from "../data/constants";
import { Button } from "../components/ui/button";
import { Upload, FileSpreadsheet, CheckCircle2, X, Loader2, Sparkles } from "lucide-react";

const phaseMeta = (key) => {
  if (key === "completed") return COMPLETED_PHASE;
  return PHASES.find((p) => p.key === key) || PHASES[0];
};

export default function Import() {
  const { t, lang, api, refresh } = useApp();
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState([]);
  const [usedAI, setUsedAI] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(false);
  const inputRef = useRef();

  const onFile = async (f) => {
    if (!f) return;
    setFileName(f.name); setApplied(false); setPreview([]); setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data.preview || []); setUsedAI(data.usedAI);
    } catch (e) {
      alert((lang === "zh" ? "解析失败: " : "Parse failed: ") + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const apply = async () => {
    const updates = preview.filter((r) => r.changed).map((r) => ({ id: r.id, toIndex: r.toIndex }));
    try {
      await api.post("/upload/apply", { updates });
      setApplied(true); await refresh();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{t("importTitle")}</h1>
        <p className="text-slate-500 text-sm">{lang === "zh" ? "上传 Excel / CSV / PDF / Word 进度报表，自动抓取关键数据更新项目阶段。" : "Upload Excel/CSV/PDF/Word to auto-update project phases."}</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-4">
          <div onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
            className="bg-white rounded-xl border-2 border-dashed border-slate-300 hover:border-brand-400 transition-colors p-10 text-center cursor-pointer">
            <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3"><Upload className="h-7 w-7 text-brand-500" /></div>
            <p className="text-slate-600 font-medium">{lang === "zh" ? "拖拽文件到此处，或点击选择" : "Drag & drop, or click to browse"}</p>
            <p className="text-xs text-slate-400 mt-1">.xlsx .xls .csv .pdf .docx</p>
            <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv,.pdf,.docx" hidden onChange={(e) => onFile(e.target.files[0])} />
          </div>

          {fileName && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-slate-800 text-sm">{fileName}</span>
                  {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                  {usedAI && <span className="flex items-center gap-1 text-[11px] text-red-600"><Sparkles className="h-3 w-3" />AI 抽取</span>}
                  {!loading && <span className="text-xs text-slate-400">· {preview.length} {lang === "zh" ? "条匹配" : "matched"}</span>}
                </div>
                <button onClick={() => { setFileName(""); setPreview([]); setApplied(false); }} className="max-md:min-h-11 max-md:min-w-11 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
              </div>
              {!loading && preview.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">{lang === "zh" ? "未匹配到可更新的项目" : "No matching projects found"}</p>}
              <div className="space-y-1.5">
                {preview.map((r) => {
                  const from = phaseMeta(r.fromPhase), to = phaseMeta(r.toPhase);
                  return (
                    <div key={r.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2">
                      <span className="text-slate-700 truncate max-w-[260px]">{r.address}</span>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="px-2 py-0.5 rounded-full" style={{ background: from.color + "22", color: from.color }}>{lang === "zh" ? from.zh : from.en}</span>
                        {r.changed ? <><span className="text-slate-400">→</span><span className="px-2 py-0.5 rounded-full font-medium" style={{ background: to.color + "22", color: to.color }}>{lang === "zh" ? to.zh : to.en}</span></> : <span className="text-slate-300">{lang === "zh" ? "无变化" : "no change"}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {preview.some((r) => r.changed) && (
                <div className="flex items-center justify-end gap-3 mt-4">
                  {applied && <span className="text-sm text-green-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> {t("updated")}</span>}
                  <Button onClick={apply} disabled={applied} className="bg-brand-500 hover:bg-brand-600 text-white">{lang === "zh" ? "应用更新" : "Apply Update"}</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
